import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import type { Mutation, MutationCache } from '@tanstack/react-query';
import { useQueuedPhotoCount, useRefusedPhotoCount } from '../../lib/upload-queue';
import { useFailedWriteCount } from '../../lib/failed-writes';
import { REPLAYABLE, wasQueuedOffline, pathOf } from '../../lib/offline-replay';
import { writesFor } from '../../lib/pending-writes';
import { isKeptForAccount } from '../../lib/write-refusals';
import { awaitingServer, mutationState } from './QueryStates';

/**
 * Whether there's a connection, and how much work is waiting on one.
 *
 * Read from TanStack's `onlineManager` rather than `navigator.onLine` on purpose: the
 * onlineManager is what actually decides whether a mutation runs or pauses, so anything
 * else on screen could disagree with what the app is doing. A banner saying "you're
 * online" over a queue that isn't draining is worse than no banner.
 *
 * MAP 225b: the manager is now SET from `navigator.onLine` at boot and on every
 * online/offline event (query.ts), so this, the gate, the write queue, the photo drain and the
 * completion barrier all share one answer. Read synchronously on every render, so the very
 * first one already knows.
 */
const subscribeOnline = (notify: () => void) => onlineManager.subscribe(() => notify());
const isOnline = () => onlineManager.isOnline();

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, isOnline, isOnline);
}

/**
 * Is this device explicitly offline? The gate's question (MAP 225a): only then may it draw
 * the saved account's screens without the server confirming who is signed in, because only
 * then can nobody new have signed in here.
 */
export function useDeviceOffline(): boolean {
  return !useOnline();
}

// ── Counting the mutation cache ──────────────────────────────────────────────

/** Moves on every event of a mutation cache: one listener per cache, for the cache's life. */
const versions = new WeakMap<MutationCache, { n: number }>();

function versionOf(cache: MutationCache): number {
  let version = versions.get(cache);
  if (!version) {
    const fresh = { n: 0 };
    cache.subscribe(() => void (fresh.n += 1));
    versions.set(cache, fresh);
    version = fresh;
  }
  return version.n;
}

/**
 * The mutations in the cache that match `predicate` (whose inputs `key` names), read
 * through `useSyncExternalStore`: recomputed from `getAll()` whenever the cache has moved
 * since the last read, and read again when the hook subscribes. The same array until then,
 * which is what lets React compare snapshots.
 *
 * Not `useMutationState`, which computes once at render and hears only LATER events: a
 * mutation built between a screen's render and its subscription (the gate rebuilding queued
 * writes as it draws) was never counted, so Take payment came back live over a queued
 * payment, and a second tap made a second payment.
 */
function useMutationList(
  predicate: (m: Mutation) => boolean,
  key: string,
): readonly Mutation[] {
  const cache = useQueryClient().getMutationCache();
  const last = useRef<{
    cache: MutationCache;
    version: number;
    key: string;
    list: readonly Mutation[];
  } | null>(null);
  const subscribe = useCallback(
    (notify: () => void) => cache.subscribe(() => notify()),
    [cache],
  );
  const list = () => {
    const version = versionOf(cache);
    const seen = last.current;
    if (seen && seen.cache === cache && seen.version === version && seen.key === key) {
      return seen.list;
    }
    const found = cache.getAll().filter(predicate);
    // The same mutations answer the same array. The cache moves on events that change none of
    // them (a screen's `useMutation` re-registering its options on every render), and a new
    // array for each would re-render that screen, which moves the cache again, without end.
    const matched =
      seen && seen.key === key && sameMembers(seen.list, found) ? seen.list : found;
    last.current = { cache, version, key, list: matched };
    return matched;
  };
  return useSyncExternalStore(subscribe, list, list);
}

const sameMembers = (a: readonly Mutation[], b: readonly Mutation[]) =>
  a.length === b.length && a.every((m, i) => m === b[i]);

/** How many mutations in the cache match `predicate`: `useMutationList`, counted. */
function useMutationCount(predicate: (m: Mutation) => boolean, key: string): number {
  return useMutationList(predicate, key).length;
}

/** What a control guarded on the queue says while the queue has not been read yet. */
export const CHECKING_LABEL = 'Checking for changes saved on this device…';

const noSubscription = () => () => {};

/**
 * Has this device's queue of saved changes been read, so the queued writes are in the cache
 * and every count below is the real one? The gate does not draw the shop until it has; this
 * is defence in depth behind it. Should a guarded control ever be drawn before the read, any
 * replayable write may be waiting unseen, so `useUnsentWrite` says one is (fail closed) and
 * the control shows CHECKING_LABEL where it would show "Saved — sends when you're back".
 * True for a client built without queued writes (nothing to read).
 */
export function useQueueRead(): boolean {
  const writes = writesFor(useQueryClient());
  const read = () => writes?.isRead() ?? true;
  return useSyncExternalStore(writes?.subscribe ?? noSubscription, read, read);
}

/**
 * How much of what a tech did with no signal the server has still not answered.
 *
 * Counted on every change to the mutation cache, which is what makes the count tick down as
 * the queue drains rather than going stale at whatever it was when the network returned.
 *
 * ⚠️ **This counted `isPaused` and that is not the same question.** `isPaused` clears the
 * instant the queue resumes — *before a single byte has been delivered* — so the count hit
 * zero on the connectivity event and the banner announced everything had gone through while
 * it was all still in flight. A **rejected** replay cleared it identically to a success.
 * `awaitingServer` covers `queued` AND `sending`, so the number now falls when the server
 * answers, which is what the banner claims to be about.
 *
 * Restricted to writes that were actually queued offline (`wasQueuedOffline`) — counting
 * every in-flight mutation would make an ordinary online save flash a bar announcing offline
 * work that never happened.
 *
 * **Two queues, one number.** Photos cannot be tRPC mutations (see `upload-queue.ts`), so
 * they live in a store of their own — and a banner that counted only the mutations would say
 * "everything you did offline has gone through" over photographs still sitting on the phone.
 * Anything a tech can do with no signal belongs in this total or the banner lies.
 */
/**
 * Work done with no signal that the server has not answered.
 *
 * Exported, and used BY the hook rather than described alongside it, so a test can pin the
 * predicate the banner actually runs. A test that re-expresses this in its own words agrees
 * with the app today and passes forever after the app changes — which is how two deliberate
 * breaks of this exact rule survived a suite that looked like it covered them.
 */
export const isUnsentOfflineWrite = (m: Mutation): boolean =>
  wasQueuedOffline(m.mutationId) && awaitingServer(m.state);

/**
 * Offline work that was refused, or that ran out of retries. Same reason it is exported.
 *
 * Never a write KEPT for its account (parked by the write guard, or a 401): that one is still
 * queued, in a paused copy of its row, and counting its failed original would announce a
 * failure for work that is waiting (MAP 225b).
 */
export const isFailedOfflineWrite = (m: Mutation): boolean =>
  wasQueuedOffline(m.mutationId) &&
  mutationState(m.state) === 'failed' &&
  !isKeptForAccount(m.state.error);

export function useQueuedWrites(): number {
  const mutations = useMutationCount(isUnsentOfflineWrite, 'unsent');
  const photos = useQueuedPhotoCount();
  return mutations + photos;
}

/**
 * Offline work the server refused, or that ran out of retries.
 *
 * Read for one reason: it is the difference between the queue **finishing** and the queue
 * **stopping**. Both take the waiting count to zero, and only one of them means the work
 * went through.
 */
export function useFailedWrites(): number {
  return useMutationCount(isFailedOfflineWrite, 'failed');
}

/**
 * Is there a write of this kind, for this thing, that the server has not answered?
 *
 * 🔴 **Asked of the QUEUE, not of the component — and that is the whole point.**
 * `useMutation()`'s state lives in a `MutationObserver` that is created fresh on every
 * mount, so a guard read from it says `idle` the moment the screen is remounted while the
 * write is still sitting in the cache. A tech records a payment with no signal, taps back to
 * their day, opens the stop again — the invoice still shows a balance, because the server
 * has not heard — and the button is live again. Tap: a second payment, with a second
 * `mutationId`, which is exactly what the idempotency key cannot dedupe.
 *
 * The same reasoning as the checklist count: `pending` is component state and dies with the
 * component, so anything that must outlive a navigation has to be read from the queue.
 *
 * `id` may be undefined while the page is still loading — the hook has to be called before
 * the early returns, and an undefined subject matches nothing.
 */
export const unsentWriteFor =
  (path: string, id: string | undefined, field: string) =>
  (m: Mutation): boolean =>
    id !== undefined &&
    pathOf(m.options.mutationKey) === path &&
    awaitingServer(m.state) &&
    (m.state.variables as Record<string, unknown> | undefined)?.[field] === id;

export function useUnsentWrite(
  path: string,
  id: string | undefined,
  field: string,
): boolean {
  const read = useQueueRead();
  const waiting = useMutationCount(
    unsentWriteFor(path, id, field),
    `${path}|${id}|${field}`,
  );
  // Not read yet: a write of this kind may be waiting unseen, so the control stays locked.
  const maybeWaiting = !read && id !== undefined && REPLAYABLE.includes(path);
  return maybeWaiting || waiting > 0;
}

/**
 * What was sent in each write of this kind, for this thing, that the server has not answered:
 * the queue's own record, oldest first, for a screen that draws what is still on the phone (a
 * part used with no signal) or bounds a control by it (what is left to put back). Read from
 * the queue for `useUnsentWrite`'s reason, and never folded into the cache with
 * `setQueryData`: a refused write leaves the queue, and the next render is right again.
 *
 * Only what the queue holds: until it has been read (`useQueueRead`) a waiting write may be
 * missing here, so a control that must fail closed asks `useUnsentWrite` too.
 */
export function useUnsentWrites(
  path: string,
  id: string | undefined,
  field: string,
): readonly Record<string, unknown>[] {
  const writes = useMutationList(
    unsentWriteFor(path, id, field),
    `list|${path}|${id}|${field}`,
  );
  return useMemo(
    () => writes.map((m) => m.state.variables as Record<string, unknown>),
    [writes],
  );
}

/**
 * Is a write of this kind, for this thing, QUEUED on the phone for want of a signal (not merely
 * on its way)? What a control says "Saved — sends when you're back" on: online, a write being
 * sent is not saved-for-later, and saying so would be the opposite lie of "Sending…" offline.
 */
export function useQueuedWrite(
  path: string,
  id: string | undefined,
  field: string,
): boolean {
  const unsent = unsentWriteFor(path, id, field);
  return (
    useMutationCount(
      (m) => unsent(m) && mutationState(m.state) === 'queued',
      `queued|${path}|${id}|${field}`,
    ) > 0
  );
}

/**
 * True once the queue has fully drained after having had something in it — the moment
 * worth confirming to a tech who has been out of signal, and only then. A toast that says
 * "everything sent" when nothing was ever queued is noise.
 *
 * ⚠️ **Work that was refused takes the waiting count to zero without having gone anywhere.**
 * From this hook's side that is identical to the queue finishing, so without the extra
 * conditions the app announces that everything went through in the same breath as the server
 * having said no — to a photo, or to a write it will not retry again.
 *
 * ⚠️ **`useFailedWrites` alone is not enough, and its shortfall is invisible for five
 * minutes.** It reads the mutation cache, which garbage-collects a failed write; once that
 * happens the count drops to zero on its own and the next drained queue announces success
 * over a payment the server refused. `useFailedWriteCount` reads the durable log, which is
 * the copy that outlives the cache — and the session, and the app being closed.
 */
export function useJustSynced(): boolean {
  const queued = useQueuedWrites();
  const failed = useFailedWrites() + useFailedWriteCount();
  const refused = useRefusedPhotoCount();
  // A ref, not state, and that is load-bearing: as a dependency it re-ran this effect the
  // moment it was cleared, and the cleanup tore down the six-second timer the same pass had
  // just created — so "Everything you did offline has gone through" stayed on screen for the
  // rest of the session.
  const hadQueue = useRef(false);
  const [justSynced, setJustSynced] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (queued > 0) {
      hadQueue.current = true;
      setJustSynced(false);
      return;
    }
    if (!hadQueue.current) return;
    hadQueue.current = false;
    setJustSynced(true);
    // Whatever the queue just wrote is not in any cached list yet.
    void qc.invalidateQueries();
    const id = setTimeout(() => setJustSynced(false), 6000);
    return () => clearTimeout(id);
  }, [queued, qc]);

  return justSynced && failed === 0 && refused === 0;
}
